// src/utils/telefonoDeContacto.js
//
// Teléfono de contacto del paso 1 de /contratar. Se guarda en `clientes/{uid}.telefono` en
// E.164 (`+56912345678`) APENAS se escribe —al salir del campo—, no al enviar: el 28-sep-2026,
// 31 de 38 registros externos aceptaron términos y se fueron de /contratar sin enviar, y de
// ninguno quedó un dato de contacto (`scripts/diagnostico/embudo.js`).
//
// Sin imports a propósito: el parser (`parsePhoneNumberFromString` de `libphonenumber-js/min`)
// se inyecta, así la suite de `functions/` ejercita esta lógica con la MISMA versión de la
// librería (`functions/__tests__/telefonoDeContacto.jest.test.js`).

export const PAIS_POR_DEFECTO = 'CL';

/**
 * Normaliza lo que la persona escribió.
 *   · `+…` → se interpreta como internacional y el selector de país no manda.
 *   · si no, se interpreta como número nacional del país elegido.
 * Devuelve `{ estado: 'vacio' | 'invalido' | 'valido', e164, pais }`. Nunca lanza.
 */
export function normalizarTelefono(texto, pais, parse) {
  const limpio = typeof texto === 'string' ? texto.trim() : '';
  if (!limpio) return { estado: 'vacio', e164: null, pais: pais || null };
  let n = null;
  try {
    n = limpio.startsWith('+') ? parse(limpio) : parse(limpio, pais || PAIS_POR_DEFECTO);
  } catch {
    n = null;
  }
  if (!n || typeof n.isValid !== 'function' || !n.isValid() || typeof n.number !== 'string') {
    return { estado: 'invalido', e164: null, pais: pais || null };
  }
  return { estado: 'valido', e164: n.number, pais: n.country || pais || null };
}

/**
 * Lista del selector: `[{ codigo, prefijo, nombre }]` ordenada por nombre en el idioma de la
 * persona. `nombreDe` puede devolver null (navegador sin `Intl.DisplayNames`): cae al código.
 */
export function listaDePaises(codigos, prefijoDe, nombreDe) {
  const lista = [];
  for (const codigo of codigos || []) {
    let prefijo = null;
    try { prefijo = prefijoDe(codigo); } catch { prefijo = null; }
    if (!prefijo) continue;
    let nombre = null;
    try { nombre = nombreDe ? nombreDe(codigo) : null; } catch { nombre = null; }
    lista.push({ codigo, prefijo: String(prefijo), nombre: nombre || codigo });
  }
  return lista.sort((a, b) => a.nombre.localeCompare(b.nombre));
}

// Espera tras la última tecla antes de guardar mientras se escribe.
export const ESPERA_GUARDADO_MS = 800;

/**
 * GUARDADO MIENTRAS SE ESCRIBE (28-sep-2026). El `onBlur` no cubre el abandono real: escribir
 * el teléfono y cerrar la pestaña o cambiar de app con el campo aún activo. En Safari de iOS
 * eso puede no disparar `blur`, y es justo el caso de los 31 que se fueron de /contratar.
 *
 *   · `programar(texto, pais)` en cada tecla: cancela lo pendiente y, SÓLO si lo escrito ya es
 *     un número válido, agenda `guardar` para dentro de `esperaMs`. Un número inválido no
 *     agenda nada (y deja cancelado lo anterior: no se guarda un estado intermedio viejo).
 *   · `ahora()` adelanta lo pendiente (respaldo al ocultarse la página). Best-effort.
 *   · `cancelar()` descarta lo pendiente (al desmontar, y al cambiar de país: lo pendiente
 *     llevaba el país anterior).
 *
 * `guardar` es la MISMA función que usa el `onBlur`; ella deduplica contra lo ya guardado.
 * Los temporizadores se inyectan para que la suite de `functions/` lo ejercite con relojes
 * falsos (`functions/__tests__/telefonoDeContacto.jest.test.js`).
 */
export function crearGuardadoDiferido({ esperaMs, esValido, guardar, setTimer, clearTimer }) {
  let timer = null;
  let pendiente = null;
  const cancelar = () => {
    if (timer !== null) { clearTimer(timer); timer = null; }
    pendiente = null;
  };
  return {
    programar(texto, pais) {
      cancelar();
      if (!esValido(texto, pais)) return false;
      pendiente = [texto, pais];
      timer = setTimer(() => {
        timer = null;
        const args = pendiente;
        pendiente = null;
        if (args) guardar(args[0], args[1]);
      }, esperaMs);
      return true;
    },
    ahora() {
      const args = pendiente;
      cancelar();
      if (!args) return false;
      guardar(args[0], args[1]);
      return true;
    },
    cancelar,
  };
}
